import type { ProductsRouter } from 'src/modules/designStudio/helpers/productSelection/ProductsRouter';
import { ColorGroupRankings } from 'src/modules/designStudio/helpers/productSelection/VariationColorGroups';
import { ProductTypesByCategory } from 'src/modules/designStudio/helpers/productSelection/constants';
import type {
  Product,
  ProductCategory,
  ProductFilter,
  ProductFilterOptions,
  ProductTypeLabel,
  Variation,
  VariationColorGroup,
} from 'src/modules/designStudio/helpers/productSelection/types';

export enum FilterOptionsFields {
  PRODUCT_TYPE = 'productTypes',
  COLOR = 'colors',
  BRAND = 'brands',
  SIZE = 'sizes',
  STYLE = 'styles',
  TEXTURE = 'textures',
}

export const createEmptyFilterOptions = (
  category: ProductCategory,
): ProductFilterOptions => ({
  productTypes: [...ProductTypesByCategory[category]],
  providers: [],
  colorGroups: [],
  sizes: [],
  styles: [],
  textures: [],
});

export const createEmptyFilter = (
  category: ProductCategory,
  productType: ProductTypeLabel | undefined = undefined,
): ProductFilter => {
  const type =
    productType === undefined
      ? [...ProductTypesByCategory[category]][0]
      : productType;
  return {
    productType: type,
    providers: [],
    colorGroups: [],
    sizes: [],
    styles: [],
    textures: [],
  };
};

export const extractExposureNumber = (size: string): number | undefined => {
  const numMarks = (size.match(/"/g) || []).length;
  if (numMarks === 1) {
    const splits = size.split('"');
    return parseFloat(splits[0]);
  }
  return undefined;
};

export const rankSizes = (sizes: string[]): string[] =>
  sizes
    .filter(s => extractExposureNumber(s) !== undefined)
    .sort((a, b) => {
      const rankOfA = extractExposureNumber(a) ?? -1;
      const rankOfB = extractExposureNumber(b) ?? -1;

      return rankOfB - rankOfA;
    });

const productSatisfiesFilterBrand = (product: Product, filter: ProductFilter) =>
  filter.providers.length === 0 ||
  filter.providers.some(uuid => uuid === product.provider.uuid);

const variationSatisfiesColor = (
  variation: Variation,
  filter: ProductFilter,
) => {
  /**
   * If our filter doesn't specify a color, then every variation satisfies the filter.
   */
  if (filter.colorGroups.length === 0) {
    return true;
  }

  /**
   * If the filter specifies a color & our variation doesnt have a color it does not satisfy the filter.
   */
  if (variation.color === undefined) {
    return false;
  }

  /**
   * If our filter specifies a color, and our variation has a color then we return true if the
   * variation has the color specified in the filter.
   */
  return filter.colorGroups.some(
    colorGroup => colorGroup === variation.color!.group,
  );
};

const variationSatisfiesFilterStyle = (
  variation: Variation,
  filter: ProductFilter,
) =>
  filter.styles.length === 0 ||
  filter.styles.some(style => style === variation.facets.style);

const variationSatisfiesFilterTexture = (
  variation: Variation,
  filter: ProductFilter,
) =>
  filter.textures.length === 0 ||
  filter.textures.some(texture => texture === variation.facets.texture);

const variationSatisfiesFilterSize = (
  variation: Variation,
  filter: ProductFilter,
) =>
  filter.sizes.length === 0 ||
  filter.sizes.some(size => size === variation.facets.sizing);

/**
 * @description Given a list of products this method will return the subset of products that satisfy the filter. When
 * the user applies filters the list of shown products is derived using this function.
 */
export const filterProducts = (
  filter: ProductFilter, // we ignore the product type, since products is already pre sorted for that
  products: Product[], // this is already sorted by product type
): Product[] => {
  const filteredProducts: Product[] = [];

  products.forEach(product => {
    if (!productSatisfiesFilterBrand(product, filter)) {
      return;
    }

    const filteredVariations: Variation[] = [];

    product.variations.forEach(variation => {
      if (
        !variationSatisfiesColor(variation, filter) ||
        !variationSatisfiesFilterStyle(variation, filter) ||
        !variationSatisfiesFilterTexture(variation, filter) ||
        !variationSatisfiesFilterSize(variation, filter)
      ) {
        return;
      }
      /**
       * At this point the variation satisfies all filter properties
       */
      filteredVariations.push(variation);
    });

    if (filteredVariations.length > 0) {
      const filteredProduct: Product = {
        ...product,
        variations: filteredVariations,
      };
      filteredProducts.push(filteredProduct);
    }
  });

  return filteredProducts;
};

export const sortColors = (
  colors: VariationColorGroup[],
): VariationColorGroup[] =>
  colors.sort((a, b) => ColorGroupRankings[b] - ColorGroupRankings[a]);

export const filterProductsFromDataStore = (
  filter: ProductFilter,
  pcDataStore: ProductsRouter,
) => filterProducts(filter, pcDataStore.getProducts(filter.productType));

/**
 * @description Given a list of products this function returns all of the filter values
 * present in the set.
 *
 * For example if you provided this function with the following products:
 *
 * products = [
 *  {manufacturer: 'JH', color: WHITE},
 *  {manufacturer: 'JH', color: GREEN},
 *  {manufacturer: 'JH', color: RED},
 *  {manufacturer: 'OC', color: WHITE}
 * ];
 *
 * const supportedFilterOptions = extractFilterOptionsFromProducts(products);
 *
 * supportedFilterOptions = {
 *  manufacturers: ['JH', 'OC'],
 *  colors: ['WHITE', 'GREEN', 'RED],
 * }
 *
 * As you can see the list of products provided included:
 * - 2 unique manufacturers
 * - 3 unique colors
 *
 * And the function therefore returned these as the valid filter options.
 *
 */
export const extractFilterOptionsFromProducts = (
  products: Product[],
): ProductFilterOptions => {
  const productTypes = new Set<ProductTypeLabel>();
  const brandsSet = new Set<string>();
  const colorsSet = new Set<string>();
  const styleSet = new Set<string>();
  const textureSet = new Set<string>();
  const sizeSet = new Set<string>();

  products.forEach(product => {
    productTypes.add(product.type);
    brandsSet.add(product.provider.uuid);
    product.variations.forEach(variation => {
      if (variation.color?.group) {
        colorsSet.add(variation.color.group);
      }

      if (variation.facets.style) {
        styleSet.add(variation.facets.style);
      }

      if (variation.facets.texture) {
        textureSet.add(variation.facets.texture);
      }

      if (variation.facets.sizing) {
        sizeSet.add(variation.facets.sizing);
      }
    });
  });

  return {
    productTypes: Array.from(productTypes.values()),
    providers: Array.from(brandsSet.values()),
    colorGroups: sortColors(
      Array.from(colorsSet.values()) as VariationColorGroup[],
    ),
    styles: Array.from(styleSet.values()),
    textures: Array.from(textureSet.values()),
    sizes: rankSizes(Array.from(sizeSet.values())),
  };
};

/**
 * @description This function is used to populate the list of valid filter options, given a users new filter selection.
 *
 * Below is how we derive the options for providers, but the same approach is used for the rest of the filter options.
 *
 * First we create a filter where the providers key is an empty array, if we
 * didn't then filterProducts() would only return products of that
 * provider type, and then extractFilterOptionsFromProducts() would
 * only return that provider.
 *
 * const filter = {
 *    providers: ['JH']
 * };
 *
 * const products = [
 *  {provider: 'JH'},
 *  {provider: 'OC'},
 *  {provider: 'BE'},
 * ];
 *
 * const filteredProducts = filterProducts(filter) = [{provider: 'JH'}];
 * extractFilterOptionsFromProducts(filteredProducts).providers = ['JH'];
 *
 * vs
 *
 *
 * const filteredProducts = filterProducts({...filter, providers: []}) =
 * [{provider: 'JH'},
 *  {provider: 'OC'},
 *  {provider: 'BE'}];
 *

 * extractFilterOptionsFromProducts(filteredProducts).providers = ['JH', 'OC', 'BE'];
 *
 */
export const deriveFilterOptions = (
  category: ProductCategory,
  filter: ProductFilter,
  productsRouter: ProductsRouter,
): ProductFilterOptions => {
  const products = productsRouter.getProducts(filter.productType);
  /**
   * The summary object tells us if there are any products available in a category,
   * due to manufacuter filtering we might not have any products in a given category.
   *
   * For example JH does not carry brick, and stone siding. In the JH guest mode we only want to show
   * JH products and therefore the user should not be able to select stone and brick.
   *
   * The summary object can tell us if there are no stone or brick options availble
   * e.x summary[BRICK] === 'NONE'
   * and so we use it to filter out avaibale productTypes.
   */
  const summary = productsRouter.getInventorySummary();

  return {
    productTypes: ProductTypesByCategory[category].filter(
      label => summary[label] !== 'NONE',
    ),
    providers: extractFilterOptionsFromProducts(
      filterProducts({ ...filter, providers: [] }, products),
    ).providers,
    colorGroups: extractFilterOptionsFromProducts(
      filterProducts({ ...filter, colorGroups: [] }, products),
    ).colorGroups,
    styles: extractFilterOptionsFromProducts(
      filterProducts({ ...filter, styles: [] }, products),
    ).styles,
    textures: extractFilterOptionsFromProducts(
      filterProducts({ ...filter, textures: [] }, products),
    ).textures,
    sizes: extractFilterOptionsFromProducts(
      filterProducts({ ...filter, sizes: [] }, products),
    ).sizes,
  };
};

export const pickAvailableProductType = (
  idealProductType: ProductTypeLabel,
  productsRouter: ProductsRouter,
  category: ProductCategory,
): ProductTypeLabel => {
  const labelsInCategory = ProductTypesByCategory[category];
  const summary = productsRouter.getInventorySummary();

  if (summary[idealProductType] === 'REAL') {
    return idealProductType;
  }

  const categoryWithRealProducts = labelsInCategory.find(
    l => summary[l] === 'REAL',
  );
  if (categoryWithRealProducts) {
    return categoryWithRealProducts;
  }

  const categoryWithGenericProducts = labelsInCategory.find(
    l => summary[l] === 'GENERIC',
  );
  if (categoryWithGenericProducts) {
    return categoryWithGenericProducts;
  }

  return idealProductType;
};
